/* Reviews & customer feedback - behaviour for tg-reviews.css components.
   Loaded with `defer` by more than one section; the guard makes the second
   copy a no-op. Everything is delegated from document, so it also covers
   content Shopify's theme editor re-renders. No autoplay, no timers. */
(() => {
  if (window.tgReviewsReady) return;
  window.tgReviewsReady = true;

  const reduceMotion = () => matchMedia('(prefers-reduced-motion: reduce)').matches;
  let lastOpener = null;

  document.addEventListener('click', (event) => {
    const t = event.target;

    // Read full review / Show less
    const expand = t.closest('[data-tg-expand]');
    if (expand) {
      const card = expand.closest('.tg-review');
      const full = card.querySelector('[data-tg-full]');
      const excerpt = card.querySelector('[data-tg-excerpt]');
      const open = expand.getAttribute('aria-expanded') !== 'true';
      full.hidden = !open;
      excerpt.hidden = open;
      expand.setAttribute('aria-expanded', String(open));
      if (!expand.dataset.more) expand.dataset.more = expand.textContent.trim();
      expand.textContent = open ? expand.dataset.less : expand.dataset.more;
      return;
    }

    // Enlarge a photo or screenshot
    const opener = t.closest('[data-tg-open]');
    if (opener) {
      const dialog = document.getElementById(opener.dataset.tgOpen);
      if (dialog && typeof dialog.showModal === 'function') {
        event.preventDefault();
        lastOpener = opener;
        // Full-size images are lazy so they cost nothing until asked for;
        // a lazy image inside a closed dialog never starts, so start it now.
        dialog.querySelectorAll('img[loading="lazy"]').forEach((img) => { img.loading = 'eager'; });
        dialog.showModal();
      }
      return;
    }

    // Click on the backdrop closes the dialog
    if (t.tagName === 'DIALOG' && t.classList.contains('tg-dialog')) {
      const r = t.getBoundingClientRect();
      if (event.clientX < r.left || event.clientX > r.right || event.clientY < r.top || event.clientY > r.bottom) t.close();
      return;
    }

    // Carousel arrows
    const arrow = t.closest('[data-tg-fb-rail-dir]');
    if (arrow) {
      const rail = arrow.closest('[data-tg-fb-rail]').querySelector('.tg-fb-rail');
      const item = rail.querySelector('.tg-fb-rail__item');
      const gap = parseFloat(getComputedStyle(rail).columnGap) || 0;
      rail.scrollBy({
        left: Number(arrow.dataset.tgFbRailDir) * (item.getBoundingClientRect().width + gap),
        behavior: reduceMotion() ? 'auto' : 'smooth'
      });
      return;
    }

    // Show all reviews
    const showAll = t.closest('[data-tg-show-all]');
    if (showAll) {
      const list = showAll.parentElement.querySelector('[data-tg-list]');
      const extra = list.querySelectorAll('[data-tg-more-item]');
      extra.forEach((li) => { li.hidden = false; });
      showAll.setAttribute('aria-expanded', 'true');
      showAll.hidden = true;
      const first = extra[0] && extra[0].querySelector('.tg-review');
      if (first) { first.setAttribute('tabindex', '-1'); first.focus(); }
      return;
    }

    // Write a review
    const toggle = t.closest('[data-tg-form-toggle]');
    if (toggle) { setForm(toggle, true); return; }
    const cancel = t.closest('[data-tg-form-cancel]');
    if (cancel) {
      const btn = document.querySelector('[data-tg-form-toggle]');
      if (btn) setForm(btn, false);
    }
  });

  // Return focus to whatever opened the dialog
  document.addEventListener('close', (event) => {
    if (event.target.classList && event.target.classList.contains('tg-dialog') && lastOpener) {
      lastOpener.focus();
      lastOpener = null;
    }
  }, true);

  function setForm(toggle, open) {
    const form = document.getElementById(toggle.getAttribute('aria-controls'));
    if (!form) return;
    form.hidden = !open;
    toggle.setAttribute('aria-expanded', String(open));
    if (open) {
      const first = form.querySelector('input[name="rating"]');
      form.scrollIntoView({ block: 'start', behavior: reduceMotion() ? 'auto' : 'smooth' });
      if (first) first.focus({ preventScroll: true });
    } else {
      toggle.focus();
    }
  }

  // Arrows are useless without scripting, so they are revealed here, and only
  // when the rail actually overflows.
  const revealArrows = () => {
    document.querySelectorAll('[data-tg-fb-rail]').forEach((wrap) => {
      const nav = wrap.querySelector('[data-tg-fb-rail-nav]');
      const rail = wrap.querySelector('.tg-fb-rail');
      if (nav && rail) nav.hidden = rail.scrollWidth <= rail.clientWidth + 4;
    });
  };
  const ready = () => {
    revealArrows();
    // Old Judge.me links (#judgeme_product_reviews) land on the new section.
    // Done here rather than with an element of that id, because Judge.me's
    // loader treats that id as a request to download its full review widget.
    if (location.hash === '#judgeme_product_reviews') {
      const target = document.getElementById('tg-reviews');
      if (target) target.scrollIntoView();
    }
    // Old links to Judge.me's widget, or #write-review, open the form.
    if (/^#(tg-write-review|write-review|judgeme_product_reviews-write)$/.test(location.hash)) {
      const btn = document.querySelector('[data-tg-form-toggle]');
      if (btn) setForm(btn, true);
    }
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', ready);
  else ready();
  addEventListener('resize', revealArrows, { passive: true });

  // '1 / 2' on the hero set follows the card in view, however it got there
  // (arrow, swipe or keyboard).
  document.addEventListener('scroll', (event) => {
    const track = event.target;
    if (!track.classList || !track.classList.contains('tg-hero-set__track')) return;
    const count = track.closest('.tg-hero-set').querySelector('[data-tg-count]');
    const total = track.children.length;
    const i = Math.min(total, Math.round(track.scrollLeft / Math.max(1, track.clientWidth)) + 1);
    if (count) count.textContent = i + ' / ' + total;
  }, true);

  // Submit to Judge.me's public create-review endpoint. Sent as a plain form
  // post so the browser makes no CORS preflight; Judge.me answers with
  // Access-Control-Allow-Origin: *. No credentials are involved.
  document.addEventListener('submit', async (event) => {
    const form = event.target.closest('[data-tg-review-form]');
    if (!form) return;
    event.preventDefault();
    const status = form.querySelector('[data-tg-form-status]');
    const submit = form.querySelector('[type="submit"]');
    const fd = new FormData(form);
    const say = (msg, kind) => {
      status.textContent = msg;
      status.className = 'tg-review-form__status' + (kind ? ' is-' + kind : '');
    };

    if (fd.get('website')) return; // honeypot
    if (!fd.get('rating')) return say('Please choose a star rating.', 'error');
    if (!String(fd.get('name') || '').trim()) return say('Please add your name.', 'error');
    const email = String(fd.get('email') || '').trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return say('Please add a valid email address.', 'error');
    if (!String(fd.get('body') || '').trim()) return say('Please write a few words about the product.', 'error');

    const params = new URLSearchParams({
      shop_domain: form.dataset.shop,
      platform: 'shopify',
      id: form.dataset.productId,
      name: String(fd.get('name')).trim(),
      email,
      rating: String(fd.get('rating')),
      title: String(fd.get('title') || '').trim(),
      body: String(fd.get('body')).trim(),
      reviewer_name_format: String(fd.get('reviewer_name_format') || '')
    });

    submit.disabled = true;
    say('Sending…');
    try {
      const res = await fetch('https://api.judge.me/api/v1/reviews', { method: 'POST', body: params });
      let data = {};
      try { data = await res.json(); } catch (e) { /* empty body */ }
      if (!res.ok) throw new Error(data.message || 'Judge.me did not accept the review');
      form.querySelectorAll('input:not([type="radio"]), textarea').forEach((el) => { el.value = ''; });
      form.querySelectorAll('input[type="radio"]').forEach((el) => { el.checked = false; });
      say('Thank you! Your review has been sent. It will appear here once it is published.', 'ok');
    } catch (err) {
      say('Sorry, your review could not be sent (' + err.message + '). Please try again in a moment.', 'error');
    } finally {
      submit.disabled = false;
    }
  });
})();
